import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { logShipmentLabelEvents } from "@/features/orders/api/ordersV4Api";
import { useZebraPrinter } from "@/features/printer/useZebraPrinter";
import {
  buildZebraShippingLabelZpl,
  mapPrintQueueOrderToZebraLabel,
} from "@/features/printer/zplBuilder";
import {
  canTrackFirestoreJobs,
  FIRESTORE_POLL_INTERVAL_MS,
  FIRESTORE_TRACK_TIMEOUT_MS,
  fetchPrinterQueueFirestoreStatus,
} from "@/shared/utils/firestoreRestJobs";

export interface TrackPrinterQueueJobOptions {
  /**
   * Fired exactly once the job reaches a terminal state — printed, failed, or timed out.
   * `attempted` is true as soon as we actually call printData(), regardless of whether the
   * printer hardware confirmed success — useful for callers that care "did we get as far as
   * trying to print" separately from "did a physical printer confirm it went through".
   */
  onSettled?: (result: { printed: boolean; attempted: boolean }) => void;
}

/**
 * Polls `printer-queue/{jobId}` until completion, then sends ZPL to Browser Print.
 */
export function useTrackPrinterQueueJob() {
  const { toast } = useToast();
  const { printData } = useZebraPrinter();
  const printerJobTrackersRef = useRef<Set<() => void>>(new Set());
  const [printerJobPollCount, setPrinterJobPollCount] = useState(0);

  useEffect(
    () => () => {
      for (const stopTracker of printerJobTrackersRef.current) {
        stopTracker();
      }
      printerJobTrackersRef.current.clear();
    },
    []
  );

  const trackPrinterQueueJob = useCallback(
    (jobId: string, options?: TrackPrinterQueueJobOptions) => {
      if (!jobId) {
        return;
      }
      if (!canTrackFirestoreJobs()) {
        toast({
          title: "Shipment queued",
          description:
            "Set Firebase env vars to receive labels automatically when the job finishes.",
          variant: "warning",
          duration: 5000,
        });
        options?.onSettled?.({ printed: false, attempted: false });
        return;
      }

      let stopped = false;
      let timer: number | undefined;
      let warnedTransientError = false;
      const startedAt = Date.now();

      const stop = () => {
        if (stopped) {
          return;
        }
        stopped = true;
        if (timer) {
          window.clearTimeout(timer);
        }
        printerJobTrackersRef.current.delete(stop);
        setPrinterJobPollCount((c) => Math.max(0, c - 1));
      };

      printerJobTrackersRef.current.add(stop);
      setPrinterJobPollCount((c) => c + 1);

      const poll = async () => {
        if (stopped) {
          return;
        }

        if (Date.now() - startedAt >= FIRESTORE_TRACK_TIMEOUT_MS) {
          stop();
          toast({
            title: "Tracking timed out",
            description: "Print job status tracking timed out.",
            variant: "warning",
            duration: 3500,
          });
          options?.onSettled?.({ printed: false, attempted: false });
          return;
        }

        try {
          const result = await fetchPrinterQueueFirestoreStatus(jobId);
          const status = result.status.toLowerCase();

          if (status === "completed") {
            let parsedPayload: unknown;
            try {
              parsedPayload = JSON.parse(result.printDataJson) as unknown;
            } catch {
              parsedPayload = [];
            }
            const rawOrders = Array.isArray(parsedPayload) ? parsedPayload : [];
            const labels = rawOrders
              .map((item) => mapPrintQueueOrderToZebraLabel(item))
              .filter((row): row is NonNullable<typeof row> => row !== null);

            let printed = false;
            let attempted = false;
            if (labels.length === 0) {
              toast({
                title: "Nothing to print",
                description: "The job finished without printable label data.",
                variant: "warning",
                duration: 4000,
              });
            } else {
              const zpl = buildZebraShippingLabelZpl(labels);
              if (!zpl.trim()) {
                toast({
                  title: "Nothing to print",
                  description: "No ZPL was produced for this job.",
                  variant: "warning",
                  duration: 4000,
                });
              } else {
                attempted = true;
                try {
                  await printData(zpl);
                  toast({
                    title: "Labels sent to printer",
                    description: "Printer dispatch completed.",
                    variant: "success",
                    duration: 2800,
                  });
                  printed = true;
                  logShipmentLabelEvents(
                    labels.map((label) => label.waybillOrOrderKey),
                    "print"
                  );
                } catch (printErr) {
                  const msg =
                    printErr instanceof Error
                      ? printErr.message
                      : "Could not send data to printer.";
                  toast({
                    title: "Print failed",
                    description: msg,
                    variant: "destructive",
                    duration: 4500,
                  });
                }
              }
            }
            stop();
            options?.onSettled?.({ printed, attempted });
            return;
          }

          if (status === "error" || status === "failed") {
            toast({
              title: "Action failed",
              description: result.errorText || "Print job failed.",
              variant: "destructive",
              duration: 4200,
            });
            stop();
            options?.onSettled?.({ printed: false, attempted: false });
            return;
          }
        } catch {
          if (!warnedTransientError) {
            warnedTransientError = true;
            toast({
              title: "Tracking retrying",
              description: "Still waiting for shipment print status…",
              variant: "warning",
              duration: 2200,
            });
          }
        }

        timer = window.setTimeout(() => void poll(), FIRESTORE_POLL_INTERVAL_MS);
      };

      void poll();
    },
    [printData, toast]
  );

  return { trackPrinterQueueJob, printerJobPollCount };
}
